import {ChangeDetectionStrategy, Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {PersistentFormDialogComponent} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.component';
import {PersistentFormDialog, PersistentFormDialogDefinition} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.types';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {PasswordFormFieldComponent} from '@aviary/forms/fields/password-form-field/password-form-field.component';
import {buildFormGroup, getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {CommonValidators, MAXIMUM_PIN_LENGTH, MINIMUM_PIN_LENGTH} from '@aviary/store/common/common.validators';
import {ConfirmedPinFormFieldComponent} from '@lib/components/confirmed-pin-form-field/confirmed-pin-form-field.component';
import {isIncorrectCurrentPinFailure} from '@store/patron/patron.helpers';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';

export interface ChangePinValue {
  currentPin: string;
  newPin: string;
}

@Component({
  selector: 'px-change-pin',
  template: `
    <mx-persistent-form-dialog [definition]="dialogDefinition">
      <mx-password-form-field fieldName="currentPin" overrideLabel="Current PIN" [includeClearButton]="false" [formGroup]="formGroup"
                              [maxLength]="maximumPinLength" inputMode="numeric"/>
      <px-confirmed-pin-form-field fieldName="newPin" pinLabel="New PIN" confirmationLabel="Confirm New PIN" [formGroup]="formGroup"/>
    </mx-persistent-form-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PersistentFormDialogComponent, PasswordFormFieldComponent, ConfirmedPinFormFieldComponent]
})
export class ChangePinDialog implements PersistentFormDialog<Record<string, never>, ChangePinValue> {
  public readonly dialogData: Record<string, never> = {};
  public readonly maximumPinLength: number = MAXIMUM_PIN_LENGTH;
  public readonly formGroup: ExtendedFormGroup = buildFormGroup<ChangePinValue>(
    {currentPin: '', newPin: ''},
    {currentPin: [Validators.required, CommonValidators.pinValidator]}
  );
  public readonly dialogDefinition: PersistentFormDialogDefinition<ChangePinValue> = {
    title: 'Change PIN',
    description: 'Use your PIN when signing in with your library card number.' +
      ` Your PIN must contain only numbers and be between ${MINIMUM_PIN_LENGTH} and ${MAXIMUM_PIN_LENGTH} digits.`,
    confirmButtonLabel: 'Change PIN',
    formGroup: this.formGroup
  };

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly currentPinControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'currentPin');
  private readonly newPinControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'newPin');

  constructor() {
    const newPinDifferentFromCurrentValidator: ValidatorFn =
      (control: AbstractControl): ValidationErrors | null =>
        isPopulatedString(control.value) && control.value === this.currentPinControl.value ? {mustDifferFromCurrentPin: true} : null;

    this.newPinControl.addValidators(newPinDifferentFromCurrentValidator);

    this.currentPinControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.newPinControl.updateValueAndValidity();
    });
  }

  public handleSubmissionFailure(error: unknown): void {
    if (!isIncorrectCurrentPinFailure(error)) return;

    this.currentPinControl.setErrors({...this.currentPinControl.errors, incorrectCurrentPin: true});
    this.currentPinControl.markAsTouched();
  }
}
